import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
    statsSection: {
        display: 'flex',
        gap: theme.spacing(2),
        margin: theme.spacing(2),
        flexWrap: 'wrap'
    },
    statsCard: {
        flex: 1,
        minWidth: 180,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        textAlign: 'center'
    },
    statsValue: {
        fontSize: '24px',
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1)
    },
    statsLabel: {
        fontSize: '14px',
        color: theme.palette.text.secondary
    },
    skeletonValue: {
        margin: '0 auto',
        marginBottom: theme.spacing(0.5)
    },
    skeletonLabel: {
        margin: '0 auto'
    }
}));

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1000, decimals = 0, suffix = '', prefix = '' }) => {
    const [displayValue, setDisplayValue] = React.useState(0);

    React.useEffect(() => {
        let startTime;
        let animationFrame;
        const startValue = 0;
        const endValue = parseFloat(value) || 0;

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);

            // Easing function
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = startValue + (endValue - startValue) * easeOutQuart;

            setDisplayValue(currentValue);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [value, duration]);

    const formattedValue = decimals > 0
        ? displayValue.toFixed(decimals)
        : Math.round(displayValue).toLocaleString();

    return <span>{prefix}{formattedValue}{suffix}</span>;
};

// Skeleton Card
const StatsCardSkeleton = ({ classes }) => (
    <Paper className={classes.statsCard} elevation={0}>
        <Skeleton variant="text" width={60} height={32} className={classes.skeletonValue} />
        <Skeleton variant="text" width={80} height={20} className={classes.skeletonLabel} />
    </Paper>
);

/**
 * StatsCards - Global Stats Cards Component
 * 
 * @param {Object} props
 * @param {Array} props.items - Array of stat items
 * @param {string} props.items[].label - Card label
 * @param {number} props.items[].value - Card value
 * @param {number} [props.items[].decimals=0] - Decimal places
 * @param {string} [props.items[].suffix=''] - Value suffix (e.g., '%')
 * @param {string} [props.items[].prefix=''] - Value prefix (e.g., '$')
 * @param {string} [props.items[].color] - Value color (hex or theme color)
 * @param {boolean} props.isLoading - Loading state
 * @param {number} [props.animationDuration=1000] - Counter animation duration in ms
 * 
 * @example
 * <StatsCards
 *     isLoading={isLoading}
 *     items={[
 *         { label: 'Total Users', value: 1250 },
 *         { label: 'Success Rate', value: 95.5, decimals: 2, suffix: '%', color: '#4caf50' },
 *         { label: 'Revenue', value: 5000, prefix: '$', color: '#2196f3' }
 *     ]}
 * />
 */
function StatsCards({ items = [], isLoading = false, animationDuration = 1000 }) {
    const classes = useStyles();

    if (isLoading) {
        return (
            <div className={classes.statsSection}>
                {items.map((_, index) => (
                    <StatsCardSkeleton key={index} classes={classes} />
                ))}
            </div>
        );
    }

    return (
        <div className={classes.statsSection}>
            {items.map((item, index) => (
                <Paper key={index} className={classes.statsCard} elevation={0}>
                    <Typography
                        className={classes.statsValue}
                        style={{ color: item.color || 'inherit' }}
                    >
                        <AnimatedCounter
                            value={item.value}
                            duration={animationDuration}
                            decimals={item.decimals || 0}
                            suffix={item.suffix || ''}
                            prefix={item.prefix || ''}
                        />
                    </Typography>
                    <Typography className={classes.statsLabel}>
                        {item.label}
                    </Typography>
                </Paper>
            ))}
        </div>
    );
}

export default React.memo(StatsCards);